import React from 'react';
import { AlertCircle } from '@/lib/icons';
import {
  Button,
  ContentContainer,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
} from 'prosci-ui';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component for catching and displaying errors in report pages
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <ContentContainer>
            <Empty>
              <EmptyIcon variant="destructive">
                <AlertCircle />
              </EmptyIcon>
              <EmptyTitle>Something went wrong</EmptyTitle>
              <EmptyDescription>
                {this.state.error?.message || 'An unexpected error occurred'}
              </EmptyDescription>
              <Button onClick={this.handleRetry}>Try Again</Button>
            </Empty>
          </ContentContainer>
        )
      );
    }

    return this.props.children;
  }
}
